/**
 * qualityApi — Quality Control endpoints.
 *
 * Sampling chooser tab (Access frmQualitySamplingSubForm2 + frmQualityQrySampling2):
 *  - cp-codes / contract-refs: dependent combo lookups (QualityQrySamplingChoose_CP / _S)
 *  - choose: read-only detail list + Sent/Count/% summary over view_factory_sampling3
 */
import { apiSlice } from './apiSlice'

export interface QualityLookupItem {
  value: string
  label: string
}

export interface SamplingChooseRow {
  id: number
  psDate: string | null
  clientLotId: string | null
  lotSizeMt: number | null
  weightPerContMt: number | null
  licenseCertNo: string | null
  result: string | null
  rejectReason: string | null
}

export interface SamplingSummaryMetric {
  value: number
  count: number
  per: number
}

export interface SamplingChooseSummary {
  buyersRef: string | null
  clientSampleRef: string | null
  deliveryTo: string | null
  quantity: number | null
  quality: string | null
  certification: string | null
  package: string | null
  total: SamplingSummaryMetric
  accept: SamplingSummaryMetric
  reject: SamplingSummaryMetric
  waiting: SamplingSummaryMetric
}

export interface SamplingChooseResponse {
  rows: SamplingChooseRow[]
  summary: SamplingChooseSummary
  total: number
}

const unwrapItems = (resp: { items?: QualityLookupItem[] } | null | undefined): QualityLookupItem[] =>
  resp && Array.isArray(resp.items) ? resp.items : []

export const qualityApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getSamplingCpCodes: b.query<QualityLookupItem[], void>({
      query: () => '/quality/sampling/cp-codes',
      transformResponse: unwrapItems,
      providesTags: [{ type: 'Quality', id: 'cp-codes' }],
    }),
    getSamplingContractRefs: b.query<QualityLookupItem[], { cpCode?: string } | void>({
      query: (arg) => ({
        url: '/quality/sampling/contract-refs',
        params: arg && arg.cpCode ? { cpCode: arg.cpCode } : undefined,
      }),
      transformResponse: unwrapItems,
      providesTags: [{ type: 'Quality', id: 'contract-refs' }],
    }),
    getSamplingChoose: b.query<SamplingChooseResponse, { cpCode?: string; contractRef?: string }>({
      query: (arg) => ({
        url: '/quality/sampling/choose',
        params: {
          ...(arg.cpCode ? { cpCode: arg.cpCode } : {}),
          ...(arg.contractRef ? { contractRef: arg.contractRef } : {}),
        },
      }),
      providesTags: [{ type: 'Quality', id: 'choose' }],
    }),
  }),
})

export const {
  useGetSamplingCpCodesQuery,
  useGetSamplingContractRefsQuery,
  useGetSamplingChooseQuery,
} = qualityApi
